import { useEffect } from "react";

import { ClientContactsEditor } from "../../clientContacts";

// ClientContactsEditModal — feature-local T-2 section-owned modal.
//
// Closed-class reference: architecture-freeze-r8.md §4 T-2
// ("modal triggered from section header; mutation owns section's
// slice; success refetches the section on close").
//
// Ownership:
//   * open/close state — owned by ClientContactsSection via plain
//     useState; this component is fully controlled.
//   * mutation — owned by ClientContactsEditor (unchanged): per-row
//     create / update / delete against the contacts endpoint.
//   * post-close freshness — owned by the section (calls its existing
//     handleReload). The parent (ClientDetailScreenContent) is NOT
//     involved and does NOT know this modal exists.
//
// Reuses the intra-feature `.feature-client-detail__modal*` CSS
// rules introduced for the T-1 ClientEditModal — same feature
// scope, not a shared modal primitive. No portal, no provider, no
// registry, no shared overlay host.
//
// Effects are limited to the allowed UI-local set per CLAUDE.md
// "Presentation Primitive Rules": ESC-key handler and body scroll
// lock. No selectors, no dispatch, no imperative handle.

function ClientContactsEditModal({ open, onClose, clientId }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-contacts-edit-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="feature-client-detail__modal-dialog">
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-contacts-edit-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i className="fa-solid fa-address-card" aria-hidden="true" />
            <span>Редагування контактів</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onClose}
            aria-label="Закрити вікно редагування контактів"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <ClientContactsEditor clientId={clientId} />
        </div>
      </div>
    </div>
  );
}

export default ClientContactsEditModal;
